/**
 * LES DONNÉES DE CETTE PIÈCE, ET RIEN D'AUTRE.
 *
 * ⛔⛔ CE FICHIER NE SE RECOPIE JAMAIS DEPUIS LE KIT. C'est le seul du dossier
 * dans ce cas, et c'est toute sa raison d'être.
 *
 * 🟢 MESURE DU 2026-09-09, et c'est elle qui a produit cette scission : les
 * données vivaient dans `certificat.ts`, avec le gabarit. La consigne
 * d'implantation dit « recopie le kit en écrasant, puis ne modifie que les
 * données » : les deux dans le même fichier, la consigne se contredisait
 * elle-même. Le même jour, DEUX recopies ont écrasé les valeurs d'une pièce en
 * production, et la seconde a remis en ligne la formulation « cette
 * application » sur le site d'une cliente dont l'ouvrage est un site vitrine.
 * Le défaut ne se voyait pas parce que la phrase SE LIT BIEN.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⭐ SIX CHAMPS À REMPLIR. LE RESTE A UNE VALEUR PAR DÉFAUT QUI TIENT.
 *
 * Une pièce qui ne renseigne que les six premiers produit un certificat
 * complet, juste et présentable. Tout ce qui suit est un enrichissement, jamais
 * une case à remplir : **un bloc vide ne s'affiche pas**, il ne laisse ni titre
 * orphelin, ni marge, ni entrée de sommaire qui ne mène nulle part.
 *
 * ⛔ C'est le principe de la page entière : elle tire sa tenue du standard et de
 * l'objet, qui sont les mêmes partout, pas d'un récit qu'il faudrait écrire à
 * chaque fois. Le sur-mesure s'ajoute quand on a quelque chose à dire.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/* ═══════════════════════════ LES SIX CHAMPS ═══════════════════════════════ */

/** Le nom de l'ouvrage, tel qu'il s'affiche en grand. */
export const OUVRAGE = "KS Rent Services";

/** Qui l'a commandé. Personne ou raison sociale, tel qu'on l'écrirait sur un acte. */
export const COMMANDITAIRE = "KS RENT SERVICES";

/**
 * Ce qu'EST la pièce, en un mot.
 *
 * ⚠️ Au singulier et SANS article : le gabarit pose « cette ${NATURE} ». Un mot
 * masculin donne donc une faute d'accord, « cette site ». Valeurs sûres :
 * `pièce`, `application`. Pour un site vitrine, écrire `pièce`, qui est de toute
 * façon le mot que la page emploie partout ailleurs.
 *
 * 🟢 Mesuré le 2026-09-09 : « application » était en dur à quatre endroits du
 * JSX, et le certificat d'un site vitrine affirmait « cette application existe
 * en un seul exemplaire ». Le défaut ne se voit pas, il faut connaître l'ouvrage
 * pour repérer que la phrase le qualifie faux.
 */
export const NATURE = "pièce";

/**
 * `NG-<millésime>-<rang>`. Un numéro n'est JAMAIS réattribué.
 *
 * ⭐ Le millésime est l'année de CERTIFICATION, pas celle de la livraison.
 * L'ancienneté de l'ouvrage est portée par `MISE_EN_SERVICE`, qui est une date
 * et non une année. Sinon le certificat porterait deux années contradictoires,
 * le millésime du numéro et l'édition du standard.
 *
 * ⭐ Le rang est celui de l'ouvrage dans l'histoire de l'atelier, pas dans
 * l'ordre de certification. La deuxième pièce certifiée porte 002 parce qu'elle
 * est le deuxième projet, pas le deuxième certificat.
 */
export const NUMERO = "NG-2026-038";

/** Depuis quand l'ouvrage est en service. C'est lui qui porte l'ancienneté. */
export const MISE_EN_SERVICE = "10 août 2025";

/** Le jour où le certificat est délivré, affiché « Certificat délivré le ». Il ne bouge plus. */
export const SORTIE_ATELIER = "20 septembre 2026";

/* ══════════════════════ CE QUI A UNE VALEUR PAR DÉFAUT ════════════════════ */

/**
 * Le nom long, s'il apporte quelque chose. `null` reprend `OUVRAGE`.
 * Exemple utile : « Laetitia Thao, site de l'assistante maternelle agréée ».
 */
export const OUVRAGE_LONG: string | null =
  "KS Rent Services, location de véhicules et chauffeur privé sur la Côte d’Azur";

/** Dernier contrôle. `null` reprend `SORTIE_ATELIER`, ce qui est le cas neuf. */
export const DERNIER_CONTROLE: string | null = null;

/**
 * Le code d'atelier du numéro segmenté, trois lettres.
 * `null` le calcule depuis les consonnes de `OUVRAGE`.
 *
 * ⚠️ À poser explicitement si le calcul tombe mal ou s'il entre en collision
 * avec une pièce déjà inscrite : deux pièces ne partagent pas un code.
 */
export const CODE_ATELIER: string | null = null;

/**
 * ⛔⛔ L'ÉTAT DE LA CERTIFICATION, ET C'EST LUI QUI SÉPARE UN STANDARD D'UN
 * BADGE. Une certification SE PERD : suspendue de plein droit dès le constat
 * d'un écart, la page l'affiche, et trente jours plus tard elle est retirée.
 * Une page qui atteste une fois pour toutes atteste en réalité qu'on ne repasse
 * jamais rien.
 *
 * ⭐ Les mentions obligatoires sont dans le type : une suspension sans article
 * en écart ni date de remise en conformité NE COMPILE PAS, et c'est voulu.
 */
export type Statut =
  | { etat: "en vigueur" }
  | {
      etat: "suspendue";
      depuis: string;
      article: string;
      conformiteAvantLe: string;
    }
  | { etat: "retirée"; le: string };

export const STATUT: Statut = { etat: "en vigueur" };

/**
 * Le renvoi au registre s'affiche-t-il ?
 *
 * ⛔ À `false` TANT QUE LA FICHE N'EXISTE PAS. 🟢 Mesuré le 2026-09-08 :
 * `next-generation.dev` répond **200 à tout chemin inconnu** en servant sa page
 * d'accueil. Le témoin `/certifications/ng-9999-999`, qui ne peut pas exister,
 * rend les mêmes octets que l'adresse réelle. Un lien actif servirait donc une
 * page commerciale à quelqu'un venu vérifier un certificat, et une faute de
 * chemin ne se verrait jamais.
 *
 * ⚠️ Typé `boolean` et non déduit : sinon TypeScript réduit au littéral et
 * déclare l'autre branche morte.
 */
export const REGISTRE_EN_LIGNE: boolean = false;

/* ═══════════════ CE QUI EST VIDE PAR DÉFAUT, ET NE S'AFFICHE PAS ══════════ */

/**
 * Les marques d'atelier tenues par cette pièce.
 *
 * ⛔ Une marque non obtenue N'Y FIGURE PAS DU TOUT, dit le standard. Vide, le
 * bloc entier disparaît : pas d'intitulé suivi de rien.
 */
export const MARQUES: readonly string[] = [];

/**
 * Le lexique du métier, posé et jamais expliqué.
 *
 * ⚠️ N'ajouter QUE des termes qui ne figurent pas déjà dans les paragraphes.
 * 🟢 Mesuré sur la pièce 002 : les huit termes du lexique étaient tous déjà
 * présents juste au-dessus. Ce n'était plus de la texture, c'était une
 * troisième énonciation de la même chose.
 */
export const LEXIQUE: readonly (readonly string[])[] = [];

/* ═════════════ LES RÉCITS, FACULTATIFS, JAMAIS DES CASES À REMPLIR ════════ */

export type RecitSurMesure = {
  titre: string;
  contexte: string;
  amorce: string;
  decisions: readonly (readonly [string, string])[];
  cloture: string;
};

export type RecitDedans = {
  titre: string;
  paragraphes: readonly string[];
  chute: string | null;
};

/**
 * Ce qui a été décidé pour cette pièce et pour personne d'autre.
 *
 * ⭐ `null` par défaut, ET C'EST LE BON DÉFAUT. Le bloc ne s'affiche pas, le
 * certificat reste complet. On l'écrit le jour où on a un fait précis à citer,
 * pas parce qu'une case attend.
 *
 * ⛔ Il porte des faits MESURÉS sur ce projet, jamais des qualités. « 183
 * créneaux, cinq jours identiques sur sept » vaut mille fois « une attention
 * particulière portée aux besoins du client ».
 *
 * ⚠️ Ne JAMAIS recopier le récit d'une autre pièce. 🟢 Le 2026-09-08, implanté
 * tel quel, le certificat d'un site vitrine affirmait « 183 créneaux », « Web
 * Push remises par APNs » et « persistance Supabase », qui sont l'histoire
 * produit d'un autre client.
 */
export const SUR_MESURE: RecitSurMesure | null = {
  titre: "Un gabarit se remplit. Ceci a été écrit.",
  contexte: `KS Rent Services fait deux métiers que le marché sépare : louer le volant, ou louer la voiture avec son chauffeur. 24 véhicules de location à la journée d’un côté, 7 classes avec chauffeur facturées à l’heure de l’autre, dans un seul catalogue. Ce n’est pas une nuance de communication : ce sont deux grilles de prix, deux intentions de recherche et deux parcours, sur le même domaine. Toute l’architecture du site part de là.`,
  amorce: "Trois décisions ont été prises pour ce catalogue, et pour lui seul.",
  decisions: [
    [
      "Aucune page de marque en dessous de trois véhicules.",
      "Le seuil n’est pas une consigne, c’est un garde-fou : la compilation échoue si on l’ignore. Huit marques du catalogue n’ont qu’une seule voiture et n’ont donc pas de page. Une page bâtie sur un véhicule est une page mince, et une page mince tire les autres vers le bas.",
    ],
    [
      "Aucun transfert proposé sans prix réel.",
      "Sept liaisons sont affichées, exactement les sept qui portent un forfait réellement pratiqué. Générer une page par couple de villes aurait été trivial, et aurait rempli le site de tarifs inventés. Le catalogue dit sept, le site dit sept.",
    ],
    [
      "Les grilles tarifaires ne contiennent aucun montant.",
      "Elles lisent le registre du moteur de devis. Les paliers de durée vivent dans une table unique indexée par le prix de base : un tarif qui change au catalogue change partout, et ne peut pas rester périmé sur une page oubliée parmi 730.",
    ],
  ],
  cloture:
    "Ces trois décisions n’ont de sens que pour une maison qui fait les deux métiers avec une flotte qu’elle possède vraiment. Retirez ce catalogue, elles n’ont plus d’objet.",
};

/**
 * Ce qu'il y a dedans. Le lexique se montre, la méthode reste à l'atelier.
 *
 * ⭐ `null` par défaut. ⛔ Ne parler QUE de cette pièce : un paragraphe sur « les
 * sites que l'atelier produit » décrit d'autres projets, sur la page qui
 * certifie celui-ci.
 */
export const DEDANS: RecitDedans | null = {
  titre: "Ce qu’il y a dedans",
  paragraphes: [
    "Next.js en App Router, pages calculées à la compilation, déployé sur Vercel. 730 pages en service : 246 en français, 241 en anglais, 243 en arabe, chacune avec son adresse canonique et ses liens de langue.",
    "Pas de base de données. Les demandes de contact et de réservation partent par courriel via Resend, avec une limitation de débit persistante sur Upstash Redis. Ce sont les deux seules dépendances extérieures capables de faire tomber une fonctionnalité, et c’est délibéré.",
    "Les données structurées de chaque page sont dérivées du contenu affiché, jamais écrites à côté : le fil d’Ariane et la foire aux questions que les moteurs lisent sont ceux que le visiteur voit. Le plan du site est régénéré à chaque compilation, et une adresse retirée est toujours redirigée, jamais supprimée.",
  ],
  chute:
    "Un visiteur n’a aucune raison de savoir tout cela. Il a une raison de sentir que la page répond vite, et qu’elle dit vrai.",
};
